import * as ImagePicker from 'expo-image-picker';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { Button, Card, IconButton, Portal, Text } from '@/components/ui';
import { spacing } from '@/styles/theme';
import { stagedAttachmentsFromFiles } from '@/utils/staged-upload';

/**
 * A staged file is one the user has picked on the new-WO form before the
 * work order has been created. The parent owns the array and uploads the
 * files after `dataService.createWorkOrder` returns a new id. (#126)
 */
export interface StagedAttachment {
	/** Local URI returned by ImagePicker / file input — may be a blob URL. */
	uri: string;
	/** Display name + upload filename. */
	name: string;
	/** Mime type as reported by the picker (best effort). */
	mimeType: string;
	/** The underlying file object for web uploads; mobile uses the URI. */
	file?: File;
}

interface AttachmentsStagingProps {
	value: StagedAttachment[];
	onChange: (next: StagedAttachment[]) => void;
	disabled?: boolean;
}

// `accept` lists the extensions alongside image/* because macOS Finder greys
// out HEIC files when only the wildcard mime is offered.
const FILE_INPUT_ACCEPT = 'image/*,.heic,.heif';

/**
 * True only for a drag carrying files. These listeners sit on `document`, so
 * they see every drag on a screen that is mostly text inputs — and reacting to
 * a text/link drag would both cover the form with the overlay and (via
 * preventDefault on dragover) stop the browser dropping that text into Work
 * Requested or Job Location. `types` is readable during the drag; `files` is
 * not populated until the drop, which is why the guard keys off `types`.
 */
const dragCarriesFiles = (dataTransfer: DataTransfer | null): boolean =>
	Array.from(dataTransfer?.types ?? []).includes('Files');

const describeRejected = (names: string[]): string => {
	const label = names.length === 1 ? '1 file was' : `${names.length} files were`;
	return `${label} skipped — only images can be attached: ${names.join(', ')}`;
};

export const AttachmentsStaging: React.FC<AttachmentsStagingProps> = ({
	value,
	onChange,
	disabled = false,
}) => {
	const theme = useTheme();
	const isWeb = Platform.OS === 'web';
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [rejected, setRejected] = useState<string[]>([]);
	// Object URLs this component minted, so unmount can release them.
	const ownedUrlsRef = useRef<Set<string>>(new Set());

	// The document listeners are bound once; this ref is how they read the
	// current props without re-binding on every keystroke in the parent form.
	// Written in an effect rather than during render so an abandoned render
	// can't leave the handlers pointing at props that never committed.
	const latest = useRef({ value, onChange, disabled });
	useEffect(() => {
		latest.current = { value, onChange, disabled };
	}, [value, onChange, disabled]);

	const revokeUrl = useCallback((uri: string) => {
		if (!isWeb || !ownedUrlsRef.current.has(uri)) return;
		ownedUrlsRef.current.delete(uri);
		URL.revokeObjectURL(uri);
	}, [isWeb]);

	/** Shared entry point for both dropped files and file-input picks. */
	const acceptBrowserFiles = useCallback((files: File[]) => {
		const current = latest.current;
		if (current.disabled || files.length === 0) return;

		const { staged, rejected: skipped } = stagedAttachmentsFromFiles(files);
		staged.forEach(item => ownedUrlsRef.current.add(item.uri));
		setRejected(skipped);
		if (staged.length > 0) {
			current.onChange([...current.value, ...staged]);
		}
	}, []);

	// Whole-page drag and drop (web only). Bound against `document` so a drop
	// anywhere on the New Work Order form counts, matching the work-order
	// detail page's attachments list. (#382)
	useEffect(() => {
		if (!isWeb || typeof document === 'undefined') return;

		// dragleave fires every time the cursor crosses a child element
		// boundary. Counting enter/leave pairs and only clearing at zero is
		// what stops the overlay strobing as the pointer moves over the form.
		let dragDepth = 0;

		const handleDragEnter = (e: DragEvent) => {
			if (!dragCarriesFiles(e.dataTransfer)) return;
			e.preventDefault();
			dragDepth += 1;
			if (!latest.current.disabled) setIsDragOver(true);
		};

		const handleDragLeave = (e: DragEvent) => {
			if (!dragCarriesFiles(e.dataTransfer)) return;
			e.preventDefault();
			dragDepth = Math.max(0, dragDepth - 1);
			if (dragDepth === 0) setIsDragOver(false);
		};

		// Without preventDefault on dragover the browser treats the drop as a
		// navigation and blows away the half-filled form.
		const handleDragOver = (e: DragEvent) => {
			if (!dragCarriesFiles(e.dataTransfer)) return;
			e.preventDefault();
			if (e.dataTransfer) e.dataTransfer.dropEffect = latest.current.disabled ? 'none' : 'copy';
		};

		const handleDrop = (e: DragEvent) => {
			if (!dragCarriesFiles(e.dataTransfer)) return;
			e.preventDefault();
			dragDepth = 0;
			setIsDragOver(false);
			const files = e.dataTransfer?.files;
			if (!files || files.length === 0) return;
			acceptBrowserFiles(Array.from(files) as File[]);
		};

		document.addEventListener('dragenter', handleDragEnter);
		document.addEventListener('dragleave', handleDragLeave);
		document.addEventListener('dragover', handleDragOver);
		document.addEventListener('drop', handleDrop);

		return () => {
			document.removeEventListener('dragenter', handleDragEnter);
			document.removeEventListener('dragleave', handleDragLeave);
			document.removeEventListener('dragover', handleDragOver);
			document.removeEventListener('drop', handleDrop);
		};
	}, [acceptBrowserFiles, isWeb]);

	// Release any object URLs still outstanding when the form goes away.
	useEffect(() => () => {
		if (Platform.OS !== 'web') return;
		ownedUrlsRef.current.forEach(uri => URL.revokeObjectURL(uri));
		ownedUrlsRef.current.clear();
	}, []);

	const handleWebFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			acceptBrowserFiles(Array.from(files) as File[]);
		}
		// Reset so re-picking the same file fires `change` again.
		event.target.value = '';
	}, [acceptBrowserFiles]);

	const handlePick = useCallback(async () => {
		if (disabled) return;

		// Web goes through a real file input: it gives OS-level multi-select
		// and hands back genuine File objects, which `resolveStagedFileForUpload`
		// uses directly instead of refetching a blob URL. (Closes PR#127-L2.)
		if (isWeb) {
			fileInputRef.current?.click();
			return;
		}

		const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
		if (status !== 'granted') return;

		const result = await ImagePicker.launchImageLibraryAsync({
			mediaTypes: ImagePicker.MediaTypeOptions.Images,
			allowsMultipleSelection: true,
			quality: 0.85,
		});
		if (result.canceled) return;

		const picked: StagedAttachment[] = result.assets.map((asset, idx) => ({
			uri: asset.uri,
			name: asset.fileName || `photo-${Date.now()}-${idx}.jpg`,
			mimeType: asset.mimeType || 'image/jpeg',
			file: (asset as unknown as { file?: File }).file,
		}));
		onChange([...value, ...picked]);
	}, [disabled, isWeb, onChange, value]);

	const handleRemove = useCallback((index: number) => {
		const removed = value[index];
		if (removed) revokeUrl(removed.uri);
		onChange(value.filter((_, i) => i !== index));
	}, [onChange, revokeUrl, value]);

	return (
		<View style={styles.container}>
			{isWeb && isDragOver && (
				<Portal>
					<View style={styles.overlay}>
						<Card
							style={[styles.overlayCard, {
								backgroundColor: theme.colors.primaryContainer,
								borderColor: theme.colors.primary,
							}]}
						>
							<IconButton icon="cloud-upload-outline" size={64} iconColor={theme.colors.primary} />
							<Text style={[styles.overlayTitle, { color: theme.colors.onPrimaryContainer }]}>
								Drop photos here
							</Text>
							<Text style={[styles.overlaySubtitle, { color: theme.colors.onPrimaryContainer }]}>
								Release to attach them to this work order
							</Text>
						</Card>
					</View>
				</Portal>
			)}

			<View style={styles.header}>
				<Text style={styles.title}>Photos (optional)</Text>
				<Button
					mode="outlined"
					icon="add"
					compact
					onPress={handlePick}
					disabled={disabled}
					testID="staged-attachments-add"
				>
					Add
				</Button>
			</View>

			{isWeb && (
				<>
					<input
						ref={fileInputRef}
						type="file"
						accept={FILE_INPUT_ACCEPT}
						multiple
						style={{ display: 'none' }}
						data-testid="staged-attachments-input"
						onChange={handleWebFileChange}
					/>
					<Pressable
						onPress={handlePick}
						disabled={disabled}
						testID="staged-attachments-dropzone"
						style={[styles.dropzone, {
							borderColor: theme.colors.primary,
							opacity: disabled ? 0.5 : 1,
						}]}
					>
						<Text style={[styles.dropzoneText, { color: theme.colors.onSurfaceVariant }]}>
							Drag photos here, or click to browse
						</Text>
					</Pressable>
				</>
			)}

			{value.length > 0 && (
				<View style={styles.list}>
					{value.map((item, i) => (
						<View key={`${item.uri}-${i}`} style={styles.chip}>
							<Text numberOfLines={1} style={styles.chipText}>{item.name}</Text>
							<IconButton
								icon="close"
								size={16}
								onPress={() => handleRemove(i)}
								accessibilityLabel={`Remove ${item.name}`}
							/>
						</View>
					))}
				</View>
			)}

			{rejected.length > 0 && (
				<Text style={[styles.hint, { color: theme.colors.error }]}>
					{describeRejected(rejected)}
				</Text>
			)}

			{value.length > 0 && isWeb && (
				<Text style={styles.hint}>
					{value.length} file{value.length === 1 ? '' : 's'} will upload after the work order is saved.
				</Text>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		marginVertical: spacing.s,
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.s,
	},
	title: {
		fontSize: 16,
		fontWeight: '600',
	},
	list: {
		gap: spacing.xs,
	},
	chip: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: spacing.s,
		paddingVertical: spacing.xs,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: 'rgba(0,0,0,0.15)',
	},
	chipText: {
		flex: 1,
		marginRight: spacing.s,
	},
	hint: {
		marginTop: spacing.s,
		fontSize: 13,
		opacity: 0.7,
	},
	dropzone: {
		borderWidth: 2,
		borderStyle: 'dashed',
		borderRadius: 8,
		paddingVertical: spacing.m,
		paddingHorizontal: spacing.s,
		marginBottom: spacing.s,
		alignItems: 'center',
	},
	dropzoneText: {
		fontSize: 13,
	},
	overlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.7)',
		zIndex: 9999,
		justifyContent: 'center',
		alignItems: 'center',
	},
	overlayCard: {
		borderWidth: 3,
		borderStyle: 'dashed',
		padding: 40,
		margin: 20,
		maxWidth: 400,
		alignItems: 'center',
	},
	overlayTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		marginTop: 16,
		textAlign: 'center',
	},
	overlaySubtitle: {
		fontSize: 16,
		marginTop: 8,
		textAlign: 'center',
		opacity: 0.8,
	},
});
